import { isContentConfig } from "../render/contentConfig.types";
import { StageRow, getStages } from "../config/pageRows.types";

// Not real entity attributes, so they must never be sent back to the update API.
const NON_ATTRIBUTE_KEYS = new Set(["id", "relatedEntities", "finalCount", "audienceCounting"]);

// Not editable record attributes, so they are excluded from the save payload.
const NON_ATTRIBUTE_FIELD_TYPES = new Set(["statusbadge", "component"]);

// Default to false, not null, when untouched: the backend reads a null boolean as true.
const BOOLEAN_FIELD_TYPES = new Set(["boolean", "checkbox"]);

export function getChangedAttributes(
  original: Record<string, unknown> | null,
  current: Record<string, unknown>
): Record<string, unknown> {
  const attributes: Record<string, unknown> = {};

  Object.keys(current).forEach((key) => {
    if (NON_ATTRIBUTE_KEYS.has(key)) {
      return;
    }

    if (current[key] !== original?.[key]) {
      attributes[key] = current[key];
    }
  });

  return attributes;
}

/**
 * Sends every field on the form, not just the ones the user touched - some
 * fields (e.g. lastname) must always be present in the payload for the save to
 * succeed, even when the user didn't change them this time.
 */
/**
 * Each field's label, by attribute name - what a validation message calls the field.
 * Taken from the config rather than read off the rendered label.
 */
export function getFieldLabels(rows: StageRow[]): Record<string, string> {
  const labels: Record<string, string> = {};

  getStages(rows).forEach((stage) =>
    stage.fields.forEach((field) => {
      if (isContentConfig(field) || !field.name) {
        return;
      }

      labels[field.name] = field.label;
    })
  );

  return labels;
}

/**
 * The validator each field names, by field name, for the fields that name one. A read-only
 * field is skipped, as the user can't fix it and it shows no error.
 */
export function getFieldValidatorKeys(rows: StageRow[]): Record<string, string> {
  const keys: Record<string, string> = {};

  getStages(rows).forEach((stage) =>
    stage.fields.forEach((field) => {
      if (isContentConfig(field) || !field.name || !field.validatorKey || field.readOnly) {
        return;
      }

      keys[field.name] = field.validatorKey;
    })
  );

  return keys;
}

export function getAllFormAttributes(
  rows: StageRow[],
  current: Record<string, unknown>
): Record<string, unknown> {
  const attributes: Record<string, unknown> = {};

  getStages(rows).forEach((stage) =>
    stage.fields.forEach((field) => {
      // A content block has no name, so it would write attributes[undefined].
      if (isContentConfig(field)) {
        return;
      }

      if (NON_ATTRIBUTE_FIELD_TYPES.has(field.type) || field.excludeFromSave) {
        return;
      }
      const fallback = BOOLEAN_FIELD_TYPES.has(field.type) ? false : null;
      attributes[field.name] = current[field.name] ?? fallback;
    })
  );

  return attributes;
}

/**
 * Most create/update endpoints validate Name separately from the attributes and
 * reject a blank one, so it's read from whichever field the config marks.
 */
export function getRecordName(
  rows: StageRow[],
  current: Record<string, unknown>
): string | undefined {
  const nameField = getStages(rows)
    .flatMap((stage) => stage.fields)
    .find((field) => !isContentConfig(field) && field.isNameField);

  if (!nameField || isContentConfig(nameField)) {
    return undefined;
  }

  const value = current[nameField.name];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
